import {
  ClientDeviceType,
  Config,
  ImageType,
  UserConfig,
  apiService,
  isTV,
  sendMetrics,
  stopStreamKO,
  stopStreamOK,
  streamPlaying,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE, logW } from "@blacknut/logging/dist";
import {
  GameStreamMetric,
  NativeModule,
  PlayerEvents,
  PlayerWebRTC,
} from "@blacknut/playerwebrtc";
import {
  Focusable,
  focusElementWithSectionPath,
  inputUI,
} from "@blacknut/spatialnav-sdk/dist";
import debounce from "lodash/debounce";
import {
  ComponentType,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import eventEmitter from "../../events/EventEmitter";
import { WebRTCError, WebRTCErrorCode } from "../../model";
import { useAppContext } from "../../utils/ContextProvider";
import { usePlayerContext } from "../../utils/PlayerContext";
import { EVENT_EMITTER_EVENT_STOP, PlayerStatus } from "../../utils/PlayerUtils";
import { useVGamepadContext } from "../../utils/VGamepadContext";
import { isTouchDevice } from "../../utils/isTouchDevice";
import VideoOverlayRelative from "../VideoOverlay/VideoOverlayRelative";
import React from "react";
import { Button, CircularProgress, useCustomBack } from "@blacknut/react-sdk/dist";
import {
  FocusableSection,
  focusSectionWithPath,
  useSpatialNavigation,
} from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import { Suspense, lazy } from "react";
import { ReactComponent as Boost } from "../../assets/frequency.svg";
import { useEricssonContext } from "../../utils/EricssonContext";
import { LOGGING_TAG } from "../../utils/logging";
import usePushNotifications from "../../utils/usePushNotifications";
import GameBackground from "../GameBackground";
import { MetricsProps } from "../Metrics/Metrics";
import Tips from "../Tips/Tips";
import VGamepadInputProvider from "../VGamepad/VGamepadInputProvider";
import { VGamepadRootViewProps } from "../VGamepad/VGamepadRootView/VGamepadRootView";
import { VideoOverlayAbsolute } from "../VideoOverlay";
import GamepadInputProvider from "./GamepadInputProvider";
import styles from "./styles.module.scss";
import useBackConfirmation from "./useBackConfirmation";
import useExitConfirmation from "./useExitConfirmation";
import useFullscreen from "./useFullscreen";
import useGameLaunching from "./useGameLaunching";
import usePointerLock from "./usePointerLock";
import useRemoteLogLevel from "./useRemoteLogLevel";

const EXIT_CONFIRM_STATUSES = [
  PlayerStatus.CONNECTING,
  PlayerStatus.CONNECTED,
  PlayerStatus.PLAYING,
  PlayerStatus.PAUSED,
  PlayerStatus.RECONNECTING,
];
interface PlayerProps {
  gameId?: string;
  streamUrl?: string; //aka llegof url
  onError: (e: WebRTCError) => void;
  onStopStream: () => void;
  codecFilter?: string[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  iceServers?: RTCIceServer[];
  acquireMicro?: boolean;
  useDesktopCrop?: boolean;
  onPlay?: () => void;
  embedded?: boolean;
  stats?: boolean;
  confirmExit?: boolean;
  statsInterval?: number;
  autoplay?: boolean;
  mute?: boolean;
  gameParameters?: string[];
  onGameLoaded?: () => void;
  onCancelLaunch?: () => void;
}

const shouldShowMetrics = (config: UserConfig | undefined, inStats?: boolean) => {
  return (!!config?.streamStats || !!inStats) && window.innerWidth > window.innerHeight;
};

const shouldShowBoostIcon = (config: Config | undefined) => {
  return !!config?.features?.ericssonBoost;
};

const _isTV = () => {
  // based on input params
  switch (apiService.userAgent.client) {
    case ClientDeviceType.TV:
      return true;
  }
  // based on ua
  return isTV();
};

let VGamepadRootView: ComponentType<VGamepadRootViewProps>;
const VGamepadRootViewFactory = (props: VGamepadRootViewProps) => {
  if (!VGamepadRootView) {
    VGamepadRootView = lazy<ComponentType<VGamepadRootViewProps>>(() => {
      return import("../VGamepad/VGamepadRootView");
    });
  }
  return <VGamepadRootView {...props} />;
};

let Metrics: ComponentType<MetricsProps>;
const MetricsFactory = (props: MetricsProps) => {
  if (!Metrics) {
    Metrics = lazy<ComponentType<MetricsProps>>(() => {
      return import("../Metrics/Metrics");
    });
  }
  return <Metrics {...props} />;
};

const Player = ({
  gameId,
  streamUrl: _streamUrl,
  onError,
  onStopStream,
  codecFilter,
  iceTransportPolicy,
  iceServers,
  acquireMicro,
  useDesktopCrop,
  onPlay,
  embedded = false,
  confirmExit = false,
  statsInterval,
  stats,
  autoplay,
  mute,
  gameParameters,
  onGameLoaded,
  onCancelLaunch,
}: PlayerProps) => {
  const player = useRef<PlayerWebRTC>();
  const caughtErrorRef = useRef(false);
  const { config, game, stream, streamUserConf } = useAppContext();
  const streamUrl = stream?.rtsp || _streamUrl;
  const { resume: resumeSpatialNav, active: spatialNavActive } = useSpatialNavigation();
  useEffect(() => {
    logD(LOGGING_TAG, "Using url %o", streamUrl);
  }, [streamUrl]);

  const {
    playerStatus,
    setPlayerStatus: _setPlayerStatus,
    fullscreen,
    setCandidatePair,
    setCandidates,
    pushMetrics,
    candidates,
    candidatePair,
    fullscreenHelper,
    preferedWindowMode,
    requestKeyboardLock,
  } = usePlayerContext();
  const {
    vGamepadVisible,
    setVGamepadVisible,
    setMiniVGamepadVisible,
    miniVGamepadVisible,
    showHideVGamepadMenu,
    setInputProvider: setVGamepadInputProvider,
  } = useVGamepadContext();

  const [autoplayError, setAutoPlayError] = useState(false);
  const statusRef = useRef(playerStatus);
  const setPlayerStatus = useCallback(
    (status: PlayerStatus) => {
      statusRef.current = status;
      _setPlayerStatus(status);
    },
    [_setPlayerStatus],
  );

  const { t } = useTranslation();

  const timeAtStartInSecond = useRef<number>();

  const gameInputProviderRef = useRef<GamepadInputProvider>();

  // Since disconnect use player status, we need to add/remove listener
  const disconnectSubscription = useRef<() => void>();

  // setup log level, use user conf if set
  useRemoteLogLevel();

  const { forceShowCursor } = usePointerLock();

  const [showMetrics, setShowMetrics] = useState(
    shouldShowMetrics(streamUserConf, stats),
  );
  const [showBoost, setShowBoost] = useState(shouldShowBoostIcon(config));

  useEffect(() => {
    setShowMetrics(shouldShowMetrics(streamUserConf, stats));
  }, [streamUserConf, stats]);

  useEffect(() => {
    setShowBoost(shouldShowBoostIcon(config));
  }, [config]);

  const _sendMetrics = useCallback(
    (metric: GameStreamMetric) => {
      if (stream) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sendMetrics(stream.id, metric as any).subscribe({
          next: () => {
            logD(LOGGING_TAG, "Send success");
          },
          error: (e) => {
            logE(LOGGING_TAG, "Send error", e);
          },
        });
        pushMetrics(metric);
      }
    },
    [pushMetrics, stream],
  );

  const playerNodeContainer = useRef<HTMLDivElement>();
  const receivedGamepadEvent = useRef(false);

  useEffect(() => {
    const resizeListener = debounce(
      () => {
        setVGamepadVisible(
          isTouchDevice() &&
            !!stream?.virtualGamepadUrl &&
            playerStatus === PlayerStatus.PLAYING &&
            window.innerWidth > window.innerHeight &&
            !receivedGamepadEvent.current,
        );

        if (playerNodeContainer.current) {
          const v = playerNodeContainer.current.querySelector("video");
          if (
            v &&
            !v.getAttribute(
              "data-scale",
            ) /* With desktop crop, a new div is created above video taking the remainig height */
          ) {
            v.style.height = "0px";

            // wait till container has been resized
            setTimeout(() => {
              v.style.height = `${playerNodeContainer.current?.offsetHeight}px`;
              v.style.maxWidth = "100%";
            }, 300);
          }
        }

        setShowMetrics(shouldShowMetrics(streamUserConf, stats));
        setShowBoost(shouldShowBoostIcon(config));
      },
      200,
      { leading: true, trailing: true },
    );
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, [
    game,
    playerStatus,
    config,
    streamUserConf,
    stats,
    setVGamepadVisible,
    stream?.virtualGamepadUrl,
  ]);

  const deletePlayer = useCallback(() => {
    disconnectSubscription.current && disconnectSubscription.current();
    disconnectSubscription.current = undefined;

    const _player = player.current;
    player.current = undefined;
    _player?.delete();
    inputUI.destroyGameGamepad();

    fullscreenHelper?.exitFullscreen().catch((e) => {
      logD(LOGGING_TAG, "ignored exception on exitFullscreen: %o ", e);
    });
  }, [fullscreenHelper]);

  // Callback when pressing back in the browser
  const onRequestStop = useCallback(() => {
    if (
      playerStatus !== PlayerStatus.EXITING &&
      playerStatus !== PlayerStatus.DISCONNECTED &&
      playerStatus !== PlayerStatus.RECONNECTING
    ) {
      if (stream) {
        logD(
          LOGGING_TAG,
          "Receiving stop streamId=%o, status = ",
          stream.id,
          playerStatus,
        );
        stopStreamOK(stream.id, "stopped", true).subscribe({
          error: (e: unknown) => {
            logE(LOGGING_TAG, "Caught error on stop", e);
          },
        });
      }
      setPlayerStatus(PlayerStatus.EXITING);

      deletePlayer();

      onStopStream();

      inputUI.setupUIGamepad();
    }
  }, [deletePlayer, onStopStream, playerStatus, setPlayerStatus, stream]);
  const onRequestCloseRef = useRef(onRequestStop);

  const { onBeforeUnload } = useExitConfirmation({
    confirmExit,
    validStatus: EXIT_CONFIRM_STATUSES,
    onConfirmed: onRequestStop,
  });

  const { push: backPush } = useCustomBack();

  useBackConfirmation({
    validStatus: EXIT_CONFIRM_STATUSES,
    onConfirmed: onRequestStop,
    simple: false,
    inputProvider: gameInputProviderRef.current,
  });

  const _onError = useCallback(
    (reason: string) => {
      logE(LOGGING_TAG, "Caught error %o", reason);
      onError({ code: WebRTCErrorCode.PLAY_ERROR, message: reason });

      if (stream) {
        stopStreamKO(stream.id, reason).subscribe({
          error: (e: unknown) => {
            logE(LOGGING_TAG, "stopStreamKO KO", e);
          },
        });
      }

      caughtErrorRef.current = true;

      setPlayerStatus(PlayerStatus.EXITING);
      deletePlayer();
      inputUI.setupUIGamepad();
    },
    [deletePlayer, onError, setPlayerStatus, stream],
  );

  const _onPlayerDisconnect = useCallback(
    (e: string) => {
      logD(LOGGING_TAG, "Received DISCONNECT event", e, playerStatus);

      if (e === "userExit") {
        // Hangup received on muskular
        onRequestStop();
        return;
      }

      if (
        (playerStatus === PlayerStatus.INIT ||
          playerStatus === PlayerStatus.CONNECTING) &&
        !caughtErrorRef.current
      ) {
        _onError("Unable to connect");
      } else if (!caughtErrorRef.current) {
        if (stream) {
          stopStreamOK(stream.id, e || "disconnected", false).subscribe({
            error: (e: unknown) => {
              logE(LOGGING_TAG, "Caught error on stop", e);
            },
          });
        }
        setPlayerStatus(PlayerStatus.EXITING);
        deletePlayer();
        onStopStream();
      }
    },
    [
      playerStatus,
      onRequestStop,
      _onError,
      stream,
      setPlayerStatus,
      deletePlayer,
      onStopStream,
    ],
  );
  useEffect(() => {
    if (
      playerStatus === PlayerStatus.CONNECTING ||
      playerStatus === PlayerStatus.CONNECTED ||
      playerStatus === PlayerStatus.PAUSED ||
      playerStatus === PlayerStatus.PLAYING ||
      playerStatus === PlayerStatus.RECONNECTING
    ) {
      eventEmitter.on(EVENT_EMITTER_EVENT_STOP, onRequestStop);
      return () => {
        eventEmitter.off(EVENT_EMITTER_EVENT_STOP, onRequestStop);
      };
    }
  }, [onRequestStop, playerStatus]);

  useEffect(() => {
    const inputProvider = gameInputProviderRef.current;
    return () => {
      if (inputProvider) {
        inputProvider.teardown();
      }
    };
  }, [stream]);

  useLayoutEffect(() => {
    resumeSpatialNav();
  }, [resumeSpatialNav]);

  useLayoutEffect(() => {
    if (playerStatus === PlayerStatus.CONNECTED) {
      if (spatialNavActive) {
        focusElementWithSectionPath(`/player`, `.${styles.play}`);
      }
    }
  }, [playerStatus, spatialNavActive]);

  useEffect(() => {
    return () => {
      // on unmount close stream
      if (
        statusRef.current !== PlayerStatus.INIT &&
        statusRef.current !== PlayerStatus.EXITING &&
        statusRef.current !== PlayerStatus.DISCONNECTED
      ) {
        onRequestCloseRef.current();
      }
    };
  }, []);

  const { boostEnabled, toggleBoost } = useEricssonContext();

  const _toggleBoost = useCallback(() => {
    toggleBoost(candidates, candidatePair);
  }, [candidates, candidatePair, toggleBoost]);

  const onClickPlay = useCallback(async () => {
    logD(LOGGING_TAG, "on Click play");
    const videoNode = playerNodeContainer.current?.querySelector(
      "video",
    ) as HTMLVideoElement;
    if (videoNode) {
      const playPromise = videoNode.play();
      if (playPromise !== undefined) {
        playPromise
          .then((_) => {
            logD(LOGGING_TAG, "Play ok");
            setAutoPlayError(false);
          })
          .catch((error) => {
            logE(LOGGING_TAG, "Play ko: %o", error);
            setAutoPlayError(true);
          });
      }
      videoNode.focus();
    }

    // Toggle Fullscreen
    if (preferedWindowMode !== "windowed") {
      try {
        await fullscreenHelper?.requestFullscreen();
      } catch (e) {
        logW(LOGGING_TAG, "Caught error requesting fullscreen", e);
      }
    } else {
      try {
        await requestKeyboardLock?.();
      } catch (e) {
        logW(LOGGING_TAG, "Caught error requesting keyboard lock", e);
      }
    }
    player.current?.onEndUserPlay();

    if (
      config?.features?.ericssonBoost &&
      config.features.ericssonBoostSubscribed === false
    ) {
      _toggleBoost();
    }
  }, [
    _toggleBoost,
    config?.features?.ericssonBoost,
    config?.features?.ericssonBoostSubscribed,
    fullscreenHelper,
    preferedWindowMode,
    requestKeyboardLock,
  ]);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      playerNodeContainer.current = node ? node : undefined;
      if (!streamUrl || player.current || statusRef.current !== PlayerStatus.INIT) {
        logD(LOGGING_TAG, "Not recreating player (status = %o)", statusRef.current);
        return;
      }
      logD(LOGGING_TAG, "Create player %o", statusRef.current);
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      const _player = new PlayerWebRTC(streamUrl, stream?.token!);
      _player.onBeforeUnload = onBeforeUnload;
      _player.mouseEnabled = !isTouchDevice();
      _player.filteredMimeTypes = codecFilter;
      _player.iceTransportPolicy = iceTransportPolicy || "all";
      _player.privateRelay = !!config?.privateRelay;
      _player.useDesktopCrop = useDesktopCrop;
      _player.iceServers = iceServers || stream?.webrtcConfig;
      _player.acquireMicro = acquireMicro || config?.features?.ericssonBoost;
      _player.userAgent = apiService.userAgent;
      _player.embedded = embedded;
      _player.streamId = stream?.id;
      if (mute) {
        _player.mute = mute;
      }
      if (fullscreenHelper) {
        _player.fullscreenHelper = fullscreenHelper;
      }
      if (statsInterval) {
        _player.statsInterval = statsInterval;
      }
      if (stream?.inputDevice) {
        _player.mouseInputMode = stream?.inputDevice;
      }
      if (preferedWindowMode) {
        _player.preferedWindowMode = preferedWindowMode;
      }
      try {
        _player.init(node);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        const reason: string = e.toString() || "unknown";
        logE(LOGGING_TAG, "Caught error creating player %o. Reason is %o", e, reason);
        _onError(reason);
        return;
      }

      _player.on(PlayerEvents.CONNECT, (e) => {
        const pair: { local: RTCIceCandidate; remote: RTCIceCandidate } | undefined =
          e.pair;
        logD(
          LOGGING_TAG,
          "Received event : CONNECT. Local pair: %o, remote : ",
          pair?.local?.toJSON(),
          pair?.remote?.toJSON(),
          e.candidates,
        );

        setCandidatePair(pair);
        if (e.candidates) {
          setCandidates(e.candidates);
        }

        if (playerNodeContainer.current) {
          const v = node?.querySelector("video");
          if (
            v &&
            !v.getAttribute(
              "data-scale",
            ) /* With desktop crop, a new div is created above video taking the remainig height */
          ) {
            v.style.height = `${playerNodeContainer.current.offsetHeight}px`;
            v.style.maxWidth = "100%";
          }
        }

        if (_player) {
          const vGamepadInputProvider = new VGamepadInputProvider(_player);
          setVGamepadInputProvider(vGamepadInputProvider);

          gameInputProviderRef.current = new GamepadInputProvider(_player, stream!);
          //On gamepad event hide virtual gamepad
          gameInputProviderRef.current.onGamepadPreEvent().subscribe({
            next: () => {
              vGamepadInputProvider.teardown();
              setVGamepadVisible(false);
              setMiniVGamepadVisible(false);
              receivedGamepadEvent.current = true;
            },
          });

          //FIX Sagemcom: connect after playing event
          if (
            statusRef.current === PlayerStatus.CONNECTING ||
            statusRef.current === PlayerStatus.RECONNECTING
          ) {
            setPlayerStatus(PlayerStatus.CONNECTED);
          }

          if (autoplay) {
            onClickPlay();
          }
        }
      });
      _player.on(PlayerEvents.IS_PLAYING, () => {
        const v = node?.querySelector("video");
        logD(
          LOGGING_TAG,
          "Received  event : IS_PLAYING %o %o",
          v?.readyState,
          statusRef.current,
        );

        if (statusRef.current !== PlayerStatus.CONNECTED) {
          logW(
            LOGGING_TAG,
            "Ignore play event, not in a valid status: %o",
            statusRef.current,
          );
          return;
        }

        if (stream) {
          streamPlaying(stream).subscribe({
            error: (e: unknown) => {
              logE(LOGGING_TAG, "streamPlaying KO", e);
            },
          });
        }
        setPlayerStatus(PlayerStatus.PLAYING);
        inputUI.destroyGamepadUI();
        if (gameInputProviderRef.current) {
          inputUI.setupGameGamepad(gameInputProviderRef.current);
        }
        onPlay?.();
        NativeModule.module?.onPlay?.();

        //
        setVGamepadVisible(
          isTouchDevice() &&
            !!stream?.virtualGamepadUrl &&
            window.innerWidth > window.innerHeight &&
            !receivedGamepadEvent.current,
        );

        timeAtStartInSecond.current = new Date().getTime() / 1000;
      });
      _player.on(PlayerEvents.RECONNNECTING, () => {
        setPlayerStatus(PlayerStatus.RECONNECTING);
      });

      _player.on(PlayerEvents.PAUSED, () => {
        logD(LOGGING_TAG, "Received  event : PAUSED");
        setPlayerStatus(PlayerStatus.PAUSED);
      });

      _player.on(PlayerEvents.SUSPENDED, () => {
        const videoNode = node?.querySelector("video");
        logD(LOGGING_TAG, "Received  event : SUSPENDED  %o", videoNode?.readyState);
        if (videoNode && videoNode.readyState >= 2) {
          setPlayerStatus(PlayerStatus.PAUSED);
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      _player.on(PlayerEvents.ERROR, (e: any) => {
        logE(LOGGING_TAG, "Received ERROR event", e);

        const reason: string = e.toString() || "unknown";
        logE(LOGGING_TAG, "REASON IS %o", reason);
        if (reason.match(/NotAllowedError/) || reason.match(/didn't interact/)) {
          setPlayerStatus(PlayerStatus.PAUSED);
        } else {
          _onError(reason);
        }
      });

      disconnectSubscription.current = _player.on(
        PlayerEvents.DISCONNECT,
        _onPlayerDisconnect,
      );

      _player.on(PlayerEvents.METRIC, _sendMetrics);

      player.current = _player;
      setPlayerStatus(PlayerStatus.CONNECTING);
    },
    [
      streamUrl,
      stream,
      onBeforeUnload,
      codecFilter,
      iceTransportPolicy,
      config?.privateRelay,
      config?.features?.ericssonBoost,
      useDesktopCrop,
      iceServers,
      acquireMicro,
      embedded,
      mute,
      fullscreenHelper,
      statsInterval,
      preferedWindowMode,
      _onPlayerDisconnect,
      _sendMetrics,
      setPlayerStatus,
      _onError,
      setCandidatePair,
      setCandidates,
      setVGamepadInputProvider,
      autoplay,
      setVGamepadVisible,
      setMiniVGamepadVisible,
      onClickPlay,
      onPlay,
    ],
  );

  // Since disconnect use player status, we need to add/remove listener
  useEffect(() => {
    if (player.current) {
      disconnectSubscription.current && disconnectSubscription.current();
      disconnectSubscription.current = player.current.on(
        PlayerEvents.DISCONNECT,
        _onPlayerDisconnect,
      );
    }
  }, [_onPlayerDisconnect]);

  usePushNotifications({
    onStop: onRequestStop,
  });
  useFullscreen();

  const onRequestCloseVideoOverlayRelative = useCallback(async () => {
    try {
      await fullscreenHelper?.requestFullscreen();
    } catch (e) {
      logE(LOGGING_TAG, "Caught error on fullscreen: %o", e);
    }
    player.current?.onEndUserResume();
    player.current?.bind();
    if (gameInputProviderRef.current) {
      inputUI.setupGameGamepad(gameInputProviderRef.current);
    }
  }, [fullscreenHelper]);

  const { gameLoading, tips } = useGameLaunching({
    gameId: gameId || "",
    gameParameters,
    onError,
    onGameLoaded,
    onCancelLaunch,
    confirmExit,
  });
  const mainImage =
    game && game.images && game.images.find((im) => im.type === ImageType.MAIN);
  return (
    <FocusableSection
      className={clsx(
        styles.main,
        apiService.userAgent.client === ClientDeviceType.TV && styles.tv,
      )}
      focusKey="player"
    >
      {gameLoading && <CircularProgress size={36} />}

      <div
        className={clsx(
          styles.container,
          embedded && styles.embedded,
          playerStatus === PlayerStatus.PLAYING && styles.playing,
          playerStatus === PlayerStatus.PAUSED && styles.paused,
        )}
        onClick={
          !vGamepadVisible && !miniVGamepadVisible ? showHideVGamepadMenu : undefined
        }
      >
        <div
          className={clsx(
            styles.webrtcClient,
            playerStatus !== PlayerStatus.PLAYING && styles.paused,
            forceShowCursor && styles.forceShowCursor,
            showMetrics && styles.hasMetrics,
          )}
          ref={setRef}
        >
          {player.current &&
            isTouchDevice() &&
            stream?.virtualGamepadUrl &&
            playerStatus === PlayerStatus.PLAYING &&
            (vGamepadVisible || miniVGamepadVisible) && (
              <Suspense fallback={null}>
                <VGamepadRootViewFactory
                  onRequestStop={onRequestStop}
                  player={player.current}
                  playerInputProvider={gameInputProviderRef.current!}
                  backPush={backPush}
                />
              </Suspense>
            )}

          {showBoost && (
            <div
              className={clsx(
                styles.boostContainer,
                (boostEnabled || false) && styles.boostActive,
                showMetrics && styles.hasMetrics,
              )}
              onClick={
                config?.features?.ericssonBoostSubscribed ? _toggleBoost : undefined
              }
            >
              <Boost />
            </div>
          )}
        </div>

        {showMetrics && (
          <Suspense fallback={null}>
            <MetricsFactory
              className={styles.metrics}
              timeAtStartInSecond={timeAtStartInSecond.current || 0}
            />
          </Suspense>
        )}

        {!isTouchDevice() &&
          !_isTV() /* no need for absolute overlay on TVs */ &&
          !fullscreen && (
            <>
              {(stream?.inputDevice === "absolute" ||
                preferedWindowMode === "windowed") &&
                player.current && (
                  <VideoOverlayAbsolute
                    className={styles.overlayAbsolute}
                    onExitPressed={onRequestStop}
                    videoNode={
                      playerNodeContainer.current?.querySelector(
                        "video",
                      ) as HTMLVideoElement
                    }
                    player={player.current}
                  />
                )}
            </>
          )}

        {!isTouchDevice() /* does not make sense to have relative input & touch device*/ &&
          !fullscreen &&
          !_isTV() /* no need for relative overlay on TVs */ &&
          stream?.inputDevice !== "absolute" &&
          preferedWindowMode !== "windowed" &&
          player.current && (
            <VideoOverlayRelative
              player={player.current}
              onExitPressed={onRequestStop}
              onRequestClose={onRequestCloseVideoOverlayRelative}
              isPlayerPlaying={playerStatus === PlayerStatus.PLAYING}
            />
          )}
      </div>

      {game &&
        (playerStatus === PlayerStatus.INIT ||
          playerStatus === PlayerStatus.CONNECTING ||
          playerStatus === PlayerStatus.CONNECTED ||
          playerStatus === PlayerStatus.RECONNECTING ||
          playerStatus === PlayerStatus.EXITING) && (
          <>
            <GameBackground game={game} />
          </>
        )}

      {game &&
        (playerStatus === PlayerStatus.INIT ||
          playerStatus === PlayerStatus.CONNECTING ||
          //https://blacknut.atlassian.net/browse/CA-3784
          (playerStatus === PlayerStatus.CONNECTED && (!autoplay || autoplayError))) && (
          <>{mainImage && <img className={styles.image} src={mainImage.url} />}</>
        )}

      {game &&
        (playerStatus === PlayerStatus.INIT ||
          playerStatus === PlayerStatus.CONNECTING) && (
          <Tips className={styles.tips} launchTips={tips} />
        )}

      {playerStatus === PlayerStatus.CONNECTED && (!autoplay || autoplayError) && (
        <Button
          variant="primary"
          className={styles.play}
          onClick={onClickPlay}
          id="play"
        >
          {config?.features?.ericssonBoost &&
          config.features.ericssonBoostSubscribed === false
            ? t("buttons.play_hd")
            : t("buttons.play")}
        </Button>
      )}
      {playerStatus === PlayerStatus.RECONNECTING && (
        <CircularProgress className={styles.reconnecting} size={36} />
      )}
    </FocusableSection>
  );
};
export default Player;
